import { Tooltip, Typography } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import AssignmentReturnedOutlinedIcon from '@material-ui/icons/AssignmentReturnedOutlined'
import BlockOutlinedIcon from '@material-ui/icons/BlockOutlined'
import CancelPresentationOutlinedIcon from '@material-ui/icons/CancelPresentationOutlined'
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircleOutlined'
import QueryBuilderOutlinedIcon from '@material-ui/icons/QueryBuilderOutlined'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import clsx from 'clsx'
import { Link } from 'react-router-dom'

import { Tags } from '@/components/tags'
import { UseGetWeedDiagnosesResult } from '@/hooks/queries/use-get-weed-diagnoses'
import { EditDiagnosisMenu } from '@/pages/weed-diagnoses/components/edit-diagnosis-menu'
import { routes } from '@/routes/config'

import { useStyles } from './styles'

type RowProps = {
  row: UseGetWeedDiagnosesResult
}

const Row = ({ row }: RowProps) => {
  const classes = useStyles()

  const STATUS_MAP = {
    registered: {
      color: classes.blueLabel,
      icon: <AssignmentReturnedOutlinedIcon />,
    },
    completed: {
      color: classes.greenLabel,
      icon: <CheckCircleOutlinedIcon />,
    },
    processing: {
      color: classes.orangeLabel,
      icon: <QueryBuilderOutlinedIcon />,
    },
    reported: {
      color: classes.darkOrangeLabel,
      icon: <ReportProblemOutlinedIcon />,
    },
    cancelled: {
      color: classes.redLabel,
      icon: <CancelPresentationOutlinedIcon />,
    },
    rejected: {
      color: classes.redLabel,
      icon: <BlockOutlinedIcon />,
    },
  }

  const statusMap = STATUS_MAP[row.status.status_key as keyof typeof STATUS_MAP]

  function addLineBreakInDescription() {
    const [firstPart, ...rest] = row.status.description.split('.')
    return (
      <>
        {firstPart}.<br />
        {rest.join('.')}
      </>
    )
  }

  const linkDisabled = !['completed', 'reported'].includes(
    row.status.status_key,
  )
  const toPossibles = {
    completed: routes.DiagnosisView(
      row.companyId.toString(),
      row.culture,
      row.treatment,
      row.id,
    ),
    reported: routes.DemandAnalysis(
      row.companyId.toString(),
      row.culture,
      row.treatment,
      row.id,
      'info',
    ),
  }

  const menuDisabled = !['registered', 'processing'].includes(
    row.status.status_key,
  )

  return (
    <TableRow
      key={row.id}
      className={clsx(classes.row, {
        [classes.disabled]: linkDisabled,
      })}
    >
      <TableCell>
        {!linkDisabled && (
          <Link
            to={toPossibles[row.status.status_key as keyof typeof toPossibles]}
            tabIndex={-1}
            className={classes.link}
          />
        )}

        <Box display='flex' gridGap='2.5rem'>
          <Typography variant='subtitle1'>
            {row.registrationDateLabel}
          </Typography>
        </Box>
      </TableCell>
      <TableCell title={row.demandName}>
        <Typography display='inline' variant='subtitle1'>
          {row.demandName}
        </Typography>
      </TableCell>
      <TableCell title={row.project.name}>
        <Box display='flex' gridGap='1.5rem'>
          <Typography variant='subtitle1'>{row.project.name}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Tags culture={row.culture} treatment={row.treatment} />
      </TableCell>
      <TableCell className={classes.statusCell}>
        <Tooltip
          title={addLineBreakInDescription()}
          placement='bottom-end'
          classes={{
            tooltip: classes.tooltip,
            popper: classes.popper,
          }}
        >
          <div className={statusMap.color}>
            <span>{statusMap.icon}</span>
            {row.status.label}
          </div>
        </Tooltip>
      </TableCell>
      <TableCell title='More'>
        <EditDiagnosisMenu
          row={{
            id: row.id,
            demandName: row.demandName,
            project: row.project,
            culture: row.culture,
            treatment: row.treatment,
            flightDate: row.flightDate,
            imageType: row.imageType,
          }}
          menuDisabled={menuDisabled}
        />
      </TableCell>
    </TableRow>
  )
}

export default Row
